import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface UIState {
  // Sidebar
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;

  // Sidebar sections the user has folded away (by NavSection id)
  collapsedNavSections: string[];
  toggleNavSection: (id: string) => void;
  expandNavSection: (id: string) => void;

  // Mobile menu
  mobileMenuOpen: boolean;
  toggleMobileMenu: () => void;
  setMobileMenuOpen: (open: boolean) => void;

  // Theme
  theme: 'light' | 'dark' | 'system';
  setTheme: (theme: 'light' | 'dark' | 'system') => void;

  // Toast notifications
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
}

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
}

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      // Sidebar
      sidebarCollapsed: false,
      toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

      collapsedNavSections: [],
      toggleNavSection: (id) =>
        set((state) => ({
          collapsedNavSections: state.collapsedNavSections.includes(id)
            ? state.collapsedNavSections.filter((s) => s !== id)
            : [...state.collapsedNavSections, id],
        })),
      expandNavSection: (id) =>
        set((state) =>
          state.collapsedNavSections.includes(id)
            ? { collapsedNavSections: state.collapsedNavSections.filter((s) => s !== id) }
            : state
        ),

      // Mobile menu
      mobileMenuOpen: false,
      toggleMobileMenu: () => set((state) => ({ mobileMenuOpen: !state.mobileMenuOpen })),
      setMobileMenuOpen: (open) => set({ mobileMenuOpen: open }),

      // Theme
      theme: 'light',
      setTheme: (theme) => set({ theme }),

      // Toasts
      toasts: [],
      addToast: (toast) => {
        const id = crypto.randomUUID();
        set((state) => ({
          toasts: [...state.toasts, { ...toast, id }],
        }));
        // Auto-remove after duration
        const duration = toast.duration ?? 5000;
        if (duration > 0) {
          setTimeout(() => {
            get().removeToast(id);
          }, duration);
        }
      },
      removeToast: (id) =>
        set((state) => ({
          toasts: state.toasts.filter((t) => t.id !== id),
        })),
    }),
    {
      name: 'flow-ui-storage',
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        collapsedNavSections: state.collapsedNavSections,
        theme: state.theme,
      }),
    }
  )
);
